import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, ExternalLink, ArrowLeft } from 'lucide-react';
import type { PhotoRecordDto } from '@forest/contracts';
import { ImageLightbox } from '@/components/ui/ImageLightbox';
import { driveThumb } from '@/lib/driveUrl';
import { safeUrl } from '@/lib/safeUrl';

type Props = {
  photo: PhotoRecordDto | null;
  onClose: () => void;
  /**
   * When set, the photo was opened from a polygon's panel: it renders on
   * top as an overlay and shows a "back to polygon" affordance. Calling
   * it returns to the polygon info instead of closing everything; the X
   * button still fully closes. Escape prefers `onBack` when present.
   */
  onBack?: () => void;
};

/**
 * Photo preview drawer / bottom sheet (RQ-10).
 * Desktop: right-side drawer. Mobile: bottom sheet.
 */
export function PhotoPreviewPanel({ photo, onClose, onBack }: Props) {
  const { t } = useTranslation(['photos', 'common']);
  const [imgFailed, setImgFailed] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setImgFailed(false);
    setLightboxOpen(false);
  }, [photo?.id]);

  useEffect(() => {
    if (!photo) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Claim the key so an enclosing Escape handler (the map's
      // fullscreen shell) doesn't also act on it.
      e.preventDefault();
      if (lightboxOpen) setLightboxOpen(false);
      else (onBack ?? onClose)();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [photo, onClose, onBack, lightboxOpen]);

  if (!photo) return null;

  const yearMonth =
    photo.month !== null && photo.year !== null
      ? `${photo.year}-${String(photo.month).padStart(2, '0')}`
      : photo.year !== null
        ? String(photo.year)
        : photo.anul !== null
          ? String(photo.anul)
          : '—';

  // `photo.project` is already a human-readable label (dynamic ProjectType
  // name, e.g. "PNERP" / "Fondul Forestier"). Legacy rows may still carry a
  // raw code ('ORANGE'/'WORLD_BANK') — those render as-is for now.
  const projectLabel = photo.project || '—';
  const authorName =
    photo.author?.cod ||
    photo.author?.fullName ||
    t('photos:author.unknown', { defaultValue: '—' });

  return (
    <>
      {/* Mobile overlay — sits above the polygon panel (z-[1150]) so the
          photo opens on top of it rather than beside/under it. */}
      <div
        className="fixed inset-0 z-[1200] bg-black/30 lg:hidden"
        onClick={onBack ?? onClose}
      />
      <aside
        className={[
          'fixed z-[1250] flex flex-col bg-white shadow-2xl',
          // Mobile/tablet: bottom sheet
          'inset-x-0 bottom-0 max-h-[80vh] rounded-t-xl',
          // Desktop: right drawer
          'lg:inset-x-auto lg:bottom-auto lg:right-0 lg:top-0 lg:h-screen lg:max-h-none lg:w-96 lg:max-w-[420px] lg:rounded-none',
        ].join(' ')}
        role="dialog"
        aria-label={t('photos:preview.title', { defaultValue: 'Previzualizare fotografie' })}
      >
        <header className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="-ml-1.5 flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                aria-label={t('photos:preview.backToPolygon', {
                  defaultValue: 'Înapoi la poligon',
                })}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t('photos:preview.polygon', { defaultValue: 'Poligon' })}
                </span>
              </button>
            ) : null}
            <h2 className="truncate text-sm font-semibold text-slate-800">
              {photo.fileName}
            </h2>
          </div>
          <button
            type="button"
            className="shrink-0 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-3 aspect-video w-full overflow-hidden rounded border border-slate-200 bg-slate-50">
            {!imgFailed && photo.downloadUrl ? (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="block h-full w-full cursor-zoom-in"
                aria-label={t('photos:preview.openLightbox', {
                  defaultValue: 'Deschide fotografia mai mare',
                })}
              >
                <img
                  src={driveThumb(photo.downloadUrl, 1200)}
                  alt={photo.fileName}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="h-full w-full object-cover"
                  onError={() => setImgFailed(true)}
                />
              </button>
            ) : (
              <div className="flex h-full w-full items-center justify-center p-4 text-center text-xs text-slate-500">
                {t('photos:preview.noThumbnail', {
                  defaultValue: 'Previzualizare indisponibilă',
                })}
              </div>
            )}
          </div>

          <dl className="space-y-2 text-xs">
            <Row label={t('photos:preview.author', { defaultValue: 'Autor' })} value={authorName} />
            <Row
              label={t('photos:preview.yearMonth', { defaultValue: 'An · Lună' })}
              value={yearMonth}
            />
            <Row
              label={t('photos:preview.project', { defaultValue: 'Proiect' })}
              value={
                <span className="inline-flex items-center rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                  {projectLabel}
                </span>
              }
            />
            {photo.projectNote ? (
              <Row
                label={t('photos:preview.projectNote', { defaultValue: 'Notă proiect' })}
                value={photo.projectNote}
              />
            ) : null}
            <Row
              label={t('photos:preview.gps', { defaultValue: 'GPS' })}
              value={
                <span className="font-mono text-[11px]">
                  {photo.latitude.toFixed(6)}, {photo.longitude.toFixed(6)}
                  {photo.gpsAccuracyM != null ? (
                    <span
                      className={`ml-2 ${
                        photo.gpsAccuracyM <= 8
                          ? 'text-emerald-600'
                          : photo.gpsAccuracyM <= 20
                            ? 'text-amber-600'
                            : 'text-red-600'
                      }`}
                    >
                      ±{Math.round(photo.gpsAccuracyM)} m
                    </span>
                  ) : null}
                </span>
              }
            />
            {photo.parcel ? (
              <>
                <Row
                  label={t('photos:table.header.ucf')}
                  value={<span className="font-mono text-[11px]">{photo.parcel.ucf}</span>}
                />
                <Row
                  label={t('photos:preview.parcel', { defaultValue: 'Poligon' })}
                  value={
                    <span className="font-mono text-[11px]">
                      {photo.parcel.codProiect ?? '—'}
                      <br />
                      <span className="text-slate-500">
                        {photo.parcel.raion} · {photo.parcel.primarie}
                      </span>
                    </span>
                  }
                />
                {/* The photo's own copies of these are nulled once it is
                    linked, so the polygon is the only place to read them. */}
                {photo.parcel.ocolSilvic ? (
                  <Row
                    label={t('photos:preview.ocolSilvic')}
                    value={photo.parcel.ocolSilvic}
                  />
                ) : null}
                {photo.parcel.entitateSilvica ? (
                  <Row
                    label={t('photos:preview.entitateSilvica')}
                    value={photo.parcel.entitateSilvica}
                  />
                ) : null}
                <Row
                  label={t('photos:preview.suprafata')}
                  value={`${photo.parcel.suprafata.toFixed(2)} ha`}
                />
                <Row label={t('photos:preview.anul')} value={String(photo.parcel.anul)} />
                {photo.subParcel ? (
                  <Row
                    label={t('photos:preview.subParcel')}
                    value={`${photo.subParcel.ucf} · ${photo.subParcel.suprafata.toFixed(2)} ha`}
                  />
                ) : null}
              </>
            ) : null}
          </dl>
        </div>

        <footer className="flex gap-2 border-t border-slate-200 p-3">
          {safeUrl(photo.downloadUrl) ? (
            <a
              href={safeUrl(photo.downloadUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded bg-emerald-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700"
            >
              <Download className="h-3.5 w-3.5" />
              {t('photos:preview.download', { defaultValue: 'Descarcă' })}
            </a>
          ) : null}
          {safeUrl(photo.downloadUrl) ? (
            <a
              href={safeUrl(photo.downloadUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1 rounded border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t('photos:preview.openInNewTab', { defaultValue: 'Tab nou' })}
            </a>
          ) : null}
        </footer>
      </aside>

      <ImageLightbox
        url={
          lightboxOpen && photo.downloadUrl && !imgFailed
            ? driveThumb(photo.downloadUrl, 2000)
            : null
        }
        alt={photo.fileName}
        caption={photo.fileName}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 border-b border-slate-100 pb-1.5 last:border-b-0">
      <dt className="w-24 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </dt>
      <dd className="flex-1 break-words text-slate-800">{value}</dd>
    </div>
  );
}
